import React, { FC, Fragment } from "react";
import { Link } from "react-router-dom";
import { Divider, makeStyles } from "@material-ui/core";
import { Button } from "../../components/core/Button";
import { useCurrentConfiguration } from "../../utils/hooks";
import { Colors } from "../../styles";
import { typeCadaster } from "../../clients/FirebaseClient/types";
import { useTypedSelector } from "../../redux";

const useComponentClasses = makeStyles(() => ({
    container: {
        marginLeft: 16,
        marginRight: 16,
        fontFamily: "innogyWEB",
        fontSize: 22.5,
        fontWeight: 300,
        lineHeight: 1.27,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        color: Colors.anthracite,
    },
    topDivider: {
        backgroundColor: Colors.anthracite5,
    },
    bottomDivider: {
        backgroundColor: Colors.anthracite25,
        marginTop: -10,
    },
    tagBox: {
        display: "flex",
        flexDirection: "row",
        height: 15,
        marginTop: 1,
    },
    tag: {
        display: "flex",
        height: "20px",
        borderRadius: "24px",
        backgroundColor: Colors.pulsingPurple,
        color: Colors.white,
        padding: "0px 8px 1px 8px",
        marginRight: "8px",
        fontFamily: "innogyWEB",
        fontSize: "14px",
        fontWeight: 500,
    },
    innerBuildingAddressContainer: {
        display: "flex",
        flexDirection: "row",
        flex: 1,
    },
    productAndAddressContainer: {
        display: "flex",
        flexDirection: "column",
    },

    innerBuildingAddressButtonContainer: {
        flex: 1,
        verticalAlign: "middle",
        alignSelf: "center",
        display: "flex",
        justifyContent: "flex-end",
        marginTop: -12,
    },
    link: {
        color: "inherit",
        textDecoration: "inherit",
    },
}));

const formatedAddressline = (
    s: string | null,
    h: string | null,
    z: string | null,
    c: string | null,
): string => {
    const sh = s && h ? s + " " + h : s || h; // Strasse und Hausnummer mit Blank dazwischen wenn beide gegeben
    const zc = z && c ? z + " " + c : z || c; // Plz und Ort mit Blank dazwischen, wenn beide gegeben
    return (sh && zc ? sh + ", " + zc : sh || zc) + ""; // Strasse Hausnummer "komma Blank" plz ort, wenn nötig
};

const formatedMarkline = (m: string | null, c: string | null, p: string | null): string => {
    return m && c ? m + ", " + c + ", " + p : m + ", " + p; // m=Mark, c=Coridor, p=Parcel
};

interface Props {
    isDeepProductCheck: boolean;
    isChangeAddressButtonVisible: boolean;
}

export const BuildingAddressHeader: FC<Props> = (props: Props) => {
    const configuration = useCurrentConfiguration();
    const componentClasses = useComponentClasses();
    const buildingAddress = configuration.state.buildingAddress;
    const { isDeepProductCheck, isChangeAddressButtonVisible } = props;

    const powerPartner = useTypedSelector(
        state =>
            state.configuration.productPartners?.power ||
            ({
                foreignProvider: false,
            } as const),
    );
    const gasPartner = useTypedSelector(
        state =>
            state.configuration.productPartners?.gas ||
            ({
                foreignProvider: false,
            } as const),
    );

    // before login we should show if not foreignProvider or address is type of cadaster
    // if we are loged in, we have to deep check, if power or gas is selected by user
    const isShowPower =
        configuration.state.productClasses?.relevant &&
        (!powerPartner.foreignProvider || buildingAddress?.state.type === typeCadaster) &&
        (!isDeepProductCheck ||
            (isDeepProductCheck && configuration.state.productClasses?.state.value.power));

    const isShowGas =
        configuration.state.productClasses?.relevant &&
        (!gasPartner.foreignProvider || buildingAddress?.state.type === typeCadaster) &&
        (!isDeepProductCheck ||
            (isDeepProductCheck && configuration.state.productClasses?.state.value.gas));

    const isShowCharger =
        configuration.state.adjustableLoadTypes?.relevant &&
        (!isDeepProductCheck ||
            (isDeepProductCheck && configuration.state.adjustableLoadTypes?.state.value.charger));

    const isShowHeatPump =
        configuration.state.adjustableLoadTypes?.relevant &&
        (!isDeepProductCheck ||
            (isDeepProductCheck && configuration.state.adjustableLoadTypes?.state.value.heatPump));

    const isShowRoomCoolingDevice =
        configuration.state.adjustableLoadTypes?.relevant &&
        (!isDeepProductCheck ||
            (isDeepProductCheck && configuration.state.adjustableLoadTypes?.state.value.roomCoolingDevice));

    return (
        <Fragment>
            {buildingAddress && (
                <div className={componentClasses.container}>
                    <Divider className={componentClasses.topDivider} />
                    <div className={componentClasses.innerBuildingAddressContainer}>
                        <div className={componentClasses.productAndAddressContainer}>
                            <div className={componentClasses.tagBox}>
                                {isShowPower && <p className={componentClasses.tag}>Strom</p>}
                                {isShowGas && <p className={componentClasses.tag}>Gas</p>}
                                {isShowCharger && <p className={componentClasses.tag}>Ladeeinrichtung</p>}
                                {isShowHeatPump && <p className={componentClasses.tag}>Wärmepumpe</p>}
                                {isShowRoomCoolingDevice && <p className={componentClasses.tag}>Gerät zur Raumkühlung</p>}
                                {configuration.state.productTypes?.state.value.newConnection && (
                                    <p className={componentClasses.tag}>Neuanschluss</p>
                                )}
                                {configuration.state.productTypes?.state.value.modification && (
                                    <p className={componentClasses.tag}>Änderung</p>
                                )}
                                {configuration.state.productTypes?.state.value.removal && (
                                    <p className={componentClasses.tag}>Demontage</p>
                                )}
                                {configuration.state.productTypes?.state.value.charger && (
                                    <p className={componentClasses.tag}>Ladeeinrichtung</p>
                                )}
                                {configuration.state.productTypes?.state.value.increase && (
                                    <p className={componentClasses.tag}>Leistungserhöhung</p>
                                )}
                                {configuration.state.productTypes?.state.value.adjustableLoad && (
                                    <p className={componentClasses.tag}>Verbrauchsgeräte</p>
                                )}
                            </div>

                            <p>
                                Ihre Bauadresse:{" "}
                                {formatedAddressline(
                                    buildingAddress.state.street?.value || "",
                                    buildingAddress.state.houseNumber?.value || "",
                                    buildingAddress.state.zipCode.value || "",
                                    buildingAddress.state.city.value || "",
                                )}
                                {buildingAddress.state.district?.value ? (
                                    <span> {buildingAddress.state.district.value || ""}</span>
                                ) : (
                                    ""
                                )}
                                {buildingAddress.state.type === typeCadaster && (
                                    <span>
                                        {" / "}
                                        {formatedMarkline(
                                            buildingAddress.state.mark.value,
                                            buildingAddress.state.corridor?.value || "",
                                            buildingAddress.state.parcel.value,
                                        )}
                                    </span>
                                )}
                            </p>
                        </div>
                        {isChangeAddressButtonVisible && (
                            <div className={componentClasses.innerBuildingAddressButtonContainer}>
                                <Link to="/address" className={componentClasses.link}>
                                    <Button mode="secondary">Bauadresse ändern</Button>
                                </Link>
                            </div>
                        )}
                    </div>
                    <Divider className={componentClasses.bottomDivider} />
                </div>
            )}
        </Fragment>
    );
};
